import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { AppStateInterface } from 'app/core/app-config/state/models';
import { EntityConnectorInterface } from 'app/core/connector-services/entity-connector-service.interface';
import { RestConnectorService } from 'app/core/connector-services/rest-connector-service/rest-connector-service.abstract';
import { TaskInterface, TaskTemplateInterface } from 'app/task/models';
import { merge, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { TaskServiceFilters } from 'app/task/services/task.service';

export class TaskRestConnectorServiceFilters {

  public assignedToId?: number;
  public completedById?: number;
  public isComplete?: boolean;
  public subjectId?: number;

  public mapFilters({
    assignedToId = undefined,
    completedById = undefined,
    isComplete = undefined,
    subjectId = undefined,
    }: TaskServiceFilters): void {
    this.assignedToId = assignedToId;
    this.completedById = completedById;
    this.isComplete = isComplete;
    this.subjectId = subjectId;
  }
}

@Injectable()
export class TaskRestConnectorService extends RestConnectorService implements EntityConnectorInterface<TaskInterface> {

  constructor(private readonly _httpClient: HttpClient,
              protected readonly _store: Store<AppStateInterface>) {
    super(_store);
    this.onInit();
  }

  public onInit(): void {}

  public loadAll(taskServiceFilters?: Readonly<TaskServiceFilters>): Observable<TaskInterface[]> {

    // map taskServiceFilters to ConnectorService specific filters
    const filters: TaskRestConnectorServiceFilters = new TaskRestConnectorServiceFilters();
    if (taskServiceFilters) {
      filters.mapFilters(taskServiceFilters);
    } else {
      filters.mapFilters(new TaskServiceFilters({
        assignedToId: this._authenticatedUser$.value.id,
        completedById: this._authenticatedUser$.value.id,
        subjectId: this._authenticatedUser$.value.id,
      }));
    }

    let tasksCompletedById: Observable<TaskInterface[]>;
    if (filters.completedById) {
      tasksCompletedById = this.loadTasksCompletedByUser(filters.completedById);
    }

    let tasksAssignedToUserId: Observable<TaskInterface[]>;
    if (filters.assignedToId) {
      tasksAssignedToUserId = this.loadTasksAssignedToUser(filters.assignedToId);
    }

    let tasksWhereTheSubjectIsUserId: Observable<TaskInterface[]>;
    if (filters.subjectId) {
      tasksWhereTheSubjectIsUserId = this.loadTasksWhereTheSubjectIsUserId(filters.subjectId);
    }

    return merge(
      tasksCompletedById,
      tasksAssignedToUserId,
      tasksWhereTheSubjectIsUserId,
    );
  }

  public loadOne(id: number): Observable<TaskInterface> {
    throw new Error('Method not implemented.');
  }

  public loadTasksAssignedToUser(userId?: number): Observable<TaskInterface[]> {
    const userIdFilter: number = (userId) ? userId : this._authenticatedUser$.value.id;

    return this._httpClient.get<TaskInterface[]>(
      `/api/users/${userIdFilter}/tasks/assigned`,
      { observe: 'response' },
    )
    .pipe(
      map((httpResponse: HttpResponse<TaskInterface[]>) => httpResponse.body),
    );
  }

  public loadTasksCompletedByUser(userId?: number): Observable<TaskInterface[]> {
    const userIdFilter: number = (userId) ? userId : this._authenticatedUser$.value.id;

    return this._httpClient.get<TaskInterface[]>(
      `/api/users/${userIdFilter}/tasks/complete`,
      { observe: 'response' },
    )
    .pipe(
      map((httpResponse: HttpResponse<TaskInterface[]>) => httpResponse.body),
    );
  }

  public loadTasksWhereTheSubjectIsUserId(userId?: number): Observable<TaskInterface[]> {
    const userIdFilter: number = (userId) ? userId : this._authenticatedUser$.value.id;

    // Current explaination in JIRA, "Users can be the subject of a Task assigned to someone else."
    return this._httpClient.get<TaskInterface[]>(
      `/api/users/${userIdFilter}/tasks/open`,
      { observe: 'response' },
    )
    .pipe(
      map((httpResponse: HttpResponse<TaskInterface[]>) => httpResponse.body),
    );
  }

  public loadTaskTemplates(): Observable<Readonly<TaskTemplateInterface[]>> {
    return this._httpClient.get<TaskTemplateInterface[]>(
      'api/tasks/templates',
      { observe: 'response' },
    )
    .pipe(
      map((httpResponse: HttpResponse<TaskTemplateInterface[]>) => {
        return httpResponse.body;
      }),
    );
  }

  public update(entity: TaskInterface): Observable<TaskInterface> {
    return this._httpClient.put<TaskInterface>(
      `/api/users/${entity.subject.id}/tasks`,
      entity,
      { observe: 'response' },
    )
    .pipe(
      map((httpResponse: HttpResponse<TaskInterface>) => {

        let task: TaskInterface;
        if (httpResponse.status === 204 && !httpResponse.body) {
          task = entity;
        } else {
          task = <TaskInterface> httpResponse.body;
        }

        return task;
      }),
    );
  }

  public close(entity: TaskInterface): Observable<TaskInterface> {
    return this._httpClient.post<TaskInterface>(
      `/api/users/${entity.subject.id}/tasks/${entity.id}/complete`,
      null,
      { observe: 'response' },
    )
    .pipe(
      map((httpResponse: HttpResponse<TaskInterface>) => {

        let task: TaskInterface;
        if (httpResponse.status === 204 && !httpResponse.body) {
          task = entity;
        } else {
          task = <TaskInterface> httpResponse.body;
        }

        return task;
      }),
    );
  }

  public create(entity: TaskInterface): Observable<TaskInterface> {
    return this._httpClient.post<TaskInterface>(
      `/api/users/${entity.subject.id}/tasks`,
      entity,
      { observe: 'response' },
    )
    .pipe(
      map((httpResponse: HttpResponse<TaskInterface>) => {

        let task: TaskInterface;
        if (httpResponse.status === 204 && !httpResponse.body) {
          task = entity;
        } else {
          task = <TaskInterface> httpResponse.body;
        }

        return task;
      }),
    );
  }

  public createSurveyTask(entity: TaskInterface): Observable<TaskInterface> {
    return this._httpClient.post<TaskInterface>(
      `/api/users/${entity.subject.id}/new-survey-task`,
      entity,
      { observe: 'response' },
    )
    .pipe(
      map((httpResponse: HttpResponse<TaskInterface>) => {

        return <TaskInterface> httpResponse.body;
      }),
    );
  }

}
